// owner-map: OP-12.spa
// Sayer's own forecasts on the new-question page. Loads the server's catalogue
// (GET /api/sayer-forecasts; the app itself never reads the data plane) and the domains the user
// forecasts in (GET /api/sayer-forecasts/interests), picks a set of cards per page load, and opens
// a card as the user's own copy (commands/sayerForecasts.ts).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRunStore } from "../store/runStore.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { openSayerForecast } from "../commands/sayerForecasts.js";
import { pickFeatured, refillSlots, snapshotItems } from "../lib/featured/featured.js";
import { ownedQuestionKeys } from "../lib/featured/observerCopy.js";
import { apiGet, apiPost } from "../lib/apiClient.js";
import { noteForecastOpen } from "../lib/telemetry/forecastOpen.js";

// Drawn once per page load: a reload shows a new set of cards, while moving around inside the
// app (New Question, opening and closing forecasts) keeps the set the user was looking at.
const PAGE_SEED = `${Date.now()}-${Math.random()}`;

// The cards wait this long for the user's domains, then draw without them.
const INTERESTS_WAIT_MS = 3000;
const TAG_WAIT_MS = 60_000;

let snapshotLoad = null;
/** The catalogue, fetched once per page; null when it is missing or unreadable. */
function loadSnapshot() {
  if (!snapshotLoad) {
    snapshotLoad = apiGet("/api/sayer-forecasts").then((r) => (r.ok ? r.body : null));
  }
  return snapshotLoad;
}

let interestsLoad = null;
/**
 * The user's domain counts, fetched once per page; {} when unavailable. When some of their
 * questions have no domain yet, the server is asked to tag them, which the next visit counts.
 */
function loadInterests() {
  if (!interestsLoad) {
    interestsLoad = apiGet("/api/sayer-forecasts/interests", {
      returnUnauthorized: true,
      signal: AbortSignal.timeout(INTERESTS_WAIT_MS),
    }).then((r) => {
      const body = r.ok && r.body && typeof r.body === "object" ? r.body : {};
      if (body.untagged > 0) {
        // telemetry-no-intent: background tagging of the user's past questions, no user operation
        void apiPost("/api/sayer-forecasts/interests/tag", undefined, {
          returnUnauthorized: true,
          signal: AbortSignal.timeout(TAG_WAIT_MS),
        });
      }
      return body.domains && typeof body.domains === "object" ? body.domains : {};
    });
  }
  return interestsLoad;
}

/**
 * @param {object} deps
 * @param {{current: ((f: object) => void)|null}} deps.handleSelectRef opens a forecast as a row click does
 * @param {(runId: string) => void} [deps.onOpened] runs before a card's forecast opens, with its
 *   copy's id (closes another forecast's settings flyover, puts the copy at the top of the list)
 * @returns {{featured: object[], searchPool: object[], openFeatured: (item: object) => void}}
 */
export function useFeaturedForecasts({ handleSelectRef, onOpened }) {
  const [snapshot, setSnapshot] = useState(null);
  // Null until loaded: the cards are drawn once, with the user's domains, so they never reshuffle
  // when the domains arrive.
  const [userDomains, setUserDomains] = useState(null);
  const inFlight = useRef(new Set());

  useEffect(() => {
    let alive = true;
    Promise.all([loadSnapshot(), loadInterests()]).then(([s, domains]) => {
      if (!alive) return;
      setSnapshot(s);
      setUserDomains(domains);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Questions already in the user's list (added from a card, or since made their own) are not
  // offered again. Joined into a string so a store update that changes no copy keeps the picks.
  const runsById = useRunStore((s) => s.runsById);
  const ownedKey = useMemo(
    () => ownedQuestionKeys(Object.values(runsById), snapshotItems(snapshot)).sort().join("\n"),
    [runsById, snapshot],
  );
  // The page's cards draw from the questions the pull marked `featured` (a catalogue without the
  // mark is all cards): the top row from the user's domains, the bottom row spread across all
  // domains; the as-you-type search covers every question in it.
  // A card the user adds gives up its slot and every other card stays where it is, so the page
  // keeps each slot's cards (refillSlots).
  const slotsRef = useRef(null);
  const featured = useMemo(() => {
    if (!userDomains) return [];
    const owned = ownedKey ? ownedKey.split("\n") : [];
    const items = snapshotItems(snapshot).filter((i) => i.featured !== false);
    const draw = (exclude) => pickFeatured(items, { userDomains, seed: PAGE_SEED, exclude });
    const { slots, cards } = refillSlots(slotsRef.current, draw, owned);
    slotsRef.current = slots;
    return cards;
  }, [snapshot, userDomains, ownedKey]);
  const searchPool = useMemo(() => {
    const owned = new Set(ownedKey ? ownedKey.split("\n") : []);
    return snapshotItems(snapshot).filter((i) => !owned.has(i.questionKey));
  }, [snapshot, ownedKey]);

  const onOpenedRef = useRef(onOpened);
  onOpenedRef.current = onOpened;
  const openFeatured = useCallback(
    async (item, { slot = null } = {}) => {
      if (!item?.questionKey || inFlight.current.has(item.questionKey)) return;
      inFlight.current.add(item.questionKey);
      try {
        await openSayerForecast(
          {
            ...makeDefaultDeps(),
            ui: {
              beforeOpen: (runId) => onOpenedRef.current?.(runId),
              open: (f) => {
                if (typeof handleSelectRef.current !== "function") return false;
                noteForecastOpen("featured");
                handleSelectRef.current(f);
                return true;
              },
            },
          },
          { item, slot },
        );
      } finally {
        inFlight.current.delete(item.questionKey);
      }
    },
    [handleSelectRef],
  );

  return { featured, searchPool, openFeatured };
}
